/**
 * The `graph_write` agent-action view — structured envelope card body.
 * Collapsed header still uses the shared changeKind badge; expanded detail
 * lists intents instead of raw Cypher.
 */
import type {
  GraphWriteEnvelopeDetail,
  InFlightAgentActionProposal
} from '@axilon-apps/domains-ontology-ui-state'
import { changeKindLabel } from '../changeKindLabel'
import { GraphMutationSettledSummary } from './graphMutationViewShared'
import { registerAgentActionView, type AgentActionViewCapability } from './viewRegistry'

type GraphWriteProposal = Extract<InFlightAgentActionProposal, { kind: 'graph_write' }>

function isGraphWrite(p: InFlightAgentActionProposal): p is GraphWriteProposal {
  return p.kind === 'graph_write'
}

function readStringProp(obj: Record<string, unknown>, key: string): string | null {
  const value = obj[key]
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

function formatSetEntries(set: Record<string, unknown> | undefined): string {
  if (!set || typeof set !== 'object') return ''
  return Object.entries(set)
    .map(([key, value]) => {
      if (value === null) return `${key} → (remove)`
      if (typeof value === 'string') return `${key} → "${value}"`
      return `${key} → ${JSON.stringify(value)}`
    })
    .join(', ')
}

function formatNodeUpsert(node: Record<string, unknown>): string {
  const label = readStringProp(node, 'label') ?? 'Node'
  const props =
    node.properties && typeof node.properties === 'object' && !Array.isArray(node.properties)
      ? (node.properties as Record<string, unknown>)
      : {}
  const name = readStringProp(props, 'name')
  const id = readStringProp(props, 'id')
  const display = name ?? id ?? 'untitled'
  return `+ ${label} "${display}"`
}

function formatRelUpsert(rel: Record<string, unknown>): string {
  const type = readStringProp(rel, 'type') ?? 'REL'
  const source = readStringProp(rel, 'sourceKey') ?? '?'
  const target = readStringProp(rel, 'targetKey') ?? '?'
  return `+ ${type} ${source} → ${target}`
}

function formatDelete(item: Record<string, unknown>): string {
  if ('node' in item && item.node && typeof item.node === 'object' && !Array.isArray(item.node)) {
    const node = item.node as Record<string, unknown>
    const label = readStringProp(node, 'label') ?? 'Node'
    const id = readStringProp(node, 'id') ?? '?'
    return `− ${label} ${id}`
  }
  if (
    'relationship' in item &&
    item.relationship &&
    typeof item.relationship === 'object' &&
    !Array.isArray(item.relationship)
  ) {
    const rel = item.relationship as Record<string, unknown>
    const type = readStringProp(rel, 'type') ?? 'REL'
    const source = readStringProp(rel, 'sourceKey') ?? '?'
    const target = readStringProp(rel, 'targetKey') ?? '?'
    return `− ${type} ${source} → ${target}`
  }
  return '− (delete)'
}

function formatOverwrite(item: Record<string, unknown>): string {
  if ('node' in item && item.node && typeof item.node === 'object' && !Array.isArray(item.node)) {
    const node = item.node as Record<string, unknown>
    const id = readStringProp(node, 'id') ?? '?'
    const set =
      node.set && typeof node.set === 'object' && !Array.isArray(node.set)
        ? (node.set as Record<string, unknown>)
        : undefined
    const sets = formatSetEntries(set)
    return sets ? `~ ${id} set ${sets}` : `~ ${id}`
  }
  if (
    'relationship' in item &&
    item.relationship &&
    typeof item.relationship === 'object' &&
    !Array.isArray(item.relationship)
  ) {
    const rel = item.relationship as Record<string, unknown>
    const type = readStringProp(rel, 'type') ?? 'REL'
    const source = readStringProp(rel, 'sourceKey') ?? '?'
    const target = readStringProp(rel, 'targetKey') ?? '?'
    const set =
      rel.set && typeof rel.set === 'object' && !Array.isArray(rel.set)
        ? (rel.set as Record<string, unknown>)
        : undefined
    const sets = formatSetEntries(set)
    return sets ? `~ ${type} ${source} → ${target} set ${sets}` : `~ ${type} ${source} → ${target}`
  }
  return '~ (overwrite)'
}

function intentLines(envelope: GraphWriteEnvelopeDetail): string[] {
  const lines: string[] = []
  for (const node of envelope.nodes ?? []) {
    if (node && typeof node === 'object' && !Array.isArray(node)) {
      lines.push(formatNodeUpsert(node))
    }
  }
  for (const rel of envelope.relationships ?? []) {
    if (rel && typeof rel === 'object' && !Array.isArray(rel)) {
      lines.push(formatRelUpsert(rel))
    }
  }
  for (const item of envelope.overwrites ?? []) {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      lines.push(formatOverwrite(item))
    }
  }
  for (const item of envelope.deletes ?? []) {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      lines.push(formatDelete(item))
    }
  }
  return lines
}

function ProposalDetail({ proposal }: { proposal: GraphWriteProposal }) {
  const lines = intentLines(proposal.envelope)
  if (lines.length === 0) {
    return (
      <div className="rounded-xl border border-line/70 bg-muted/30 p-2 text-11 text-muted-foreground">
        (empty envelope)
      </div>
    )
  }
  return (
    <div className="rounded-xl border border-line/70 bg-muted/30 p-2 text-11 text-foreground">
      <ul className="grid gap-1">
        {lines.map((line, index) => (
          <li key={`${index}-${line}`} className="break-words font-mono text-10">
            {line}
          </li>
        ))}
      </ul>
    </div>
  )
}

export const graphWriteViewCapability: AgentActionViewCapability = {
  kind: 'graph_write',
  badgeLabel(proposal) {
    const kind: string = proposal.kind
    return isGraphWrite(proposal) ? changeKindLabel(proposal.changeKind) : kind
  },
  renderDetail(proposal) {
    if (!isGraphWrite(proposal)) return null
    return <ProposalDetail proposal={proposal} />
  },
  renderSettledSummary(proposal) {
    if (!isGraphWrite(proposal)) return null
    return <GraphMutationSettledSummary proposal={proposal} />
  }
}

registerAgentActionView(graphWriteViewCapability)
